import { motion } from 'framer-motion';
import { useRef, useEffect } from 'react';
import { ArrowRight } from 'lucide-react';

export const CTASection = () => {
  const sectionRef = useRef(null);
  const auroraRef = useRef(null);

  useEffect(() => {
    const onMove = (e) => {
      if (!sectionRef.current || !auroraRef.current) return;
      const r = sectionRef.current.getBoundingClientRect();
      const x = ((e.clientX - r.left) / r.width) * 100;
      const y = ((e.clientY - r.top) / r.height) * 100;
      auroraRef.current.style.setProperty('--mx', `${x}%`);
      auroraRef.current.style.setProperty('--my', `${y}%`);
    };
    window.addEventListener('mousemove', onMove);
    return () => window.removeEventListener('mousemove', onMove);
  }, []);

  const scrollToContact = () => {
    const el = document.querySelector('#contact');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section
      ref={sectionRef}
      className="relative overflow-hidden bg-[#2563EB]"
      data-testid="cta-section"
    >
      {/* Mouse-reactive aurora — lighter overlay on dark bg */}
      <div ref={auroraRef} className="absolute inset-0 pointer-events-none" style={{
        background: 'radial-gradient(ellipse 700px 500px at var(--mx, 50%) var(--my, 50%), rgba(255,255,255,0.18) 0%, rgba(255,255,255,0.08) 30%, transparent 60%)',
        transition: 'background 200ms ease-out',
      }} />

      <div className="container-custom py-36 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="max-w-4xl"
        >
          <div className="flex items-center gap-3 mb-8">
            <span className="font-mono text-[0.65rem] tracking-[0.22em] uppercase font-medium text-[#93C5FD]">
              Let's Work Together
            </span>
            <span className="w-6 h-px bg-white/40" />
          </div>

          <h2
            className="font-display font-bold text-white leading-tight mb-8"
            style={{ fontSize: 'clamp(2.5rem, 6vw, 5rem)' }}
          >
            Let's Build Something{' '}
            <span className="italic text-white/85">Smart</span>{' '}
            Together
          </h2>

          <div className="h-px bg-gradient-to-r from-transparent via-white/40 to-transparent mb-10" />

          <p className="text-white/80 text-lg max-w-2xl mb-12 leading-relaxed">
            Ready to transform your business with cutting-edge technology?
            Book a free consultation and let's discuss how we can help you achieve your goals.
          </p>

          <div className="flex flex-wrap gap-5 items-center">
            <motion.button
              onClick={scrollToContact}
              whileHover={{ scale: 1.03, y: -2 }}
              whileTap={{ scale: 0.98 }}
              className="group inline-flex items-center gap-3 px-8 py-4 rounded-full bg-white text-[#2563EB] font-semibold hover:bg-[#F6F9FC] transition-colors duration-300 shadow-[0_8px_30px_rgba(0,0,0,0.18)]"
              data-testid="cta-start-project-btn"
            >
              Start Your Project
              <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
            </motion.button>
            <motion.button
              onClick={scrollToContact}
              whileHover={{ scale: 1.03 }}
              className="inline-flex items-center gap-3 px-8 py-4 rounded-full border border-white/30 text-white text-sm font-medium hover:border-white/60 hover:bg-white/10 transition-all duration-300 backdrop-blur-sm"
            >
              Book a Free Call
            </motion.button>
          </div>

          <p className="mt-8 font-mono text-[0.6rem] tracking-widest text-white/50 uppercase">
            Free consultation · No commitment · Response within 24 hours
          </p>
        </motion.div>
      </div>

      {/* Ghost text */}
      <div className="absolute right-0 bottom-0 pointer-events-none overflow-hidden select-none">
        <span
          className="font-display font-bold italic leading-none"
          style={{
            fontSize: 'clamp(8rem, 20vw, 18rem)',
            color: 'rgba(255,255,255,0.05)',
            userSelect: 'none',
          }}
        >
          Smart
        </span>
      </div>
    </section>
  );
};
